import { forwardRef, useCallback, useId, useRef } from 'react'

import { useTranslation } from 'react-i18n-lite'

import Input from '~/components/DataFields/Input'
import Icon from '~/components/Icon'

import useActiveOption from '~/hooks/useActiveOption'
import useClickOutside from '~/hooks/useClickOutside'
import useIsOpen from '~/hooks/useIsOpen'

import SelectionMenu, { SelectionMenuOption } from '../SelectionMenu'

import {
  ArrowButton,
  ComponentStructure,
  SpinningIcon
} from './StandardUniselection.styles'

import { StandardUniselectionProps } from './StandardUniselection.type'

export default forwardRef<HTMLInputElement, StandardUniselectionProps>(
  function StandardUniselection(
    {
      statusClass = 'default',
      heightClass = 'regular',
      disabled,
      informational,
      options,
      onSelect,
      selectedOption,
      ...props
    },
    ref
  ) {
    const selectionMenuId = useId()

    const componentStructureRef = useRef(null)

    const inputRef = useRef<HTMLInputElement>(null)

    const focusInput = () => {
      if ('current' in inputRef) {
        inputRef.current?.focus()
      }
    }

    const { isOpen, open, close } = useIsOpen(false)

    const { t } = useTranslation()

    useClickOutside(componentStructureRef, () => close())

    const {
      activeOptionIndex,
      activeOptionId,
      setNextActiveOption,
      setPreviousActiveOption
    } = useActiveOption({
      value: '',
      isOpen,
      options,
      collapseSelected: false
    })

    const toggleMenu = useCallback(
      () => (isOpen ? close() : open()),
      [isOpen, open, close]
    )

    const handleSelect = (
      option: SelectionMenuOption,
      cleanOption?: boolean
    ) => {
      focusInput()

      onSelect(cleanOption ? undefined : option)

      close()
    }

    const handleClick = () => {
      if (informational || disabled) {
        return
      }

      toggleMenu()
    }

    const onKeyDown: React.KeyboardEventHandler = (event) => {
      open()

      const selectActiveOption = () =>
        options[activeOptionIndex] && handleSelect(options[activeOptionIndex])

      const actionsByKey = {
        Enter: selectActiveOption,
        ArrowDown: setNextActiveOption,
        ArrowUp: setPreviousActiveOption
      }

      if (event.key in actionsByKey) {
        event.preventDefault()

        actionsByKey[event.key as keyof typeof actionsByKey]()
      }
    }

    const rightAreaElement = useCallback(() => {
      return (
        !informational && (
          <>
            {statusClass === 'processing' && (
              <SpinningIcon iconName='Spinner' />
            )}

            <ArrowButton
              isOpen={isOpen}
              disabled={disabled}
              onClick={toggleMenu}
              title={
                isOpen
                  ? t('uniselection.hide-options')
                  : t('uniselection.show-options')
              }
            >
              <Icon
                iconName={
                  disabled
                    ? 'DropdownArrowDownCircleDashed'
                    : 'DropdownArrowDownCircle'
                }
                preserveFocusOnParent
              />
            </ArrowButton>
          </>
        )
      )
    }, [informational, isOpen, disabled, statusClass, toggleMenu, t])

    return (
      <ComponentStructure ref={componentStructureRef}>
        <Input
          {...props}
          statusClass={statusClass}
          rightAreaElement={rightAreaElement}
          onKeyDown={onKeyDown}
          disabled={disabled}
          informational={informational}
          heightClass={heightClass}
          value={selectedOption?.label || selectedOption?.value || ''}
          role='combobox'
          aria-haspopup='listbox'
          aria-activedescendant={activeOptionId}
          aria-controls={selectionMenuId}
          aria-expanded={isOpen}
          aria-busy={statusClass === 'processing'}
          ref={ref || inputRef}
          autoComplete='off'
          onClick={handleClick}
          readOnly
        />

        {isOpen && !disabled && !informational && (
          <SelectionMenu
            id={selectionMenuId}
            uniselection
            onSelect={handleSelect}
            selectedOptions={selectedOption ? [selectedOption] : []}
            options={options}
            heightClass={heightClass}
            activeOptionId={activeOptionId}
            statusClass={statusClass}
          />
        )}
      </ComponentStructure>
    )
  }
)
